// uploader.ts — the drag-drop uploader built into the Upload pane. Multi-file, with client-side
// previews; posts a FormData (ajax=1) to the upload endpoint and hands the returned media back so
// the manager appends them live into the matching gallery — no reload. One generic implementation
// drives both kinds: 'image' (→ Pictures gallery) and 'video' (→ Videos gallery), differing only
// in accept types, the file field name, the endpoint, the preview element, and the response shape.

import type { ItemMediaConfig, ItemPicture, ItemVideo } from './types';

type UploadKind = 'image' | 'video' | 'media' | 'icon';

// A bare uploaded file (icon kind): no DB row, no gallery — just the CDN url it landed at.
export interface UploadedFile {
    name: string;
    url: string | null;
}

interface PictureUploadResponse {
    ok: boolean;
    uploaded: Array<{ pic_id: string; url: string | null; name: string; primary: boolean }>;
    errors: string[];
}

interface IconUploadResponse {
    ok: boolean;
    uploaded: Array<{ name: string; url: string | null }>;
    errors: string[];
}

interface VideoUploadResponse {
    ok: boolean;
    uploaded: Array<{
        video_id: string;
        url: string | null;
        mime: string;
        name: string;
        description: string;
        primary: boolean;
    }>;
    errors: string[];
}

export interface UploaderHooks {
    config: ItemMediaConfig;
    itemId: string;                                 // target item (from data-ft-media); may be '' if none yet
    kind: UploadKind;
    onUploadedPictures?: (pics: ItemPicture[]) => void;
    onUploadedVideos?: (vids: ItemVideo[]) => void;
    onUploadedIcons?: (icons: UploadedFile[]) => void;
}

interface KindSpec {
    accept: string;
    fieldName: string;       // multipart file field the endpoint reads
    endpoint: (c: ItemMediaConfig) => string;
    dropTitle: string;
    dropSub: string;
    noun: string;            // for the result message ("image"/"video")
    matches: (f: File) => boolean;
}

const SPECS: Record<UploadKind, KindSpec> = {
    image: {
        accept: 'image/png, image/gif, image/jpeg, image/webp',
        fieldName: 'file[]',
        endpoint: (c) => c.uploadEndpoint,
        dropTitle: 'Drag & drop images here',
        dropSub: 'or click to browse — PNG, JPG, GIF, WebP',
        noun: 'image',
        matches: (f) => f.type.startsWith('image/'),
    },
    video: {
        accept: 'video/mp4',
        fieldName: 'video_file[]',
        endpoint: (c) => c.videoUploadEndpoint,
        dropTitle: 'Drag & drop videos here',
        dropSub: 'or click to browse — MP4',
        noun: 'video',
        matches: (f) => f.type === 'video/mp4' || /\.mp4$/i.test(f.name),
    },
    media: {
        accept: 'image/png, image/gif, image/jpeg, image/webp, video/mp4',
        fieldName: 'file[]',
        endpoint: (c) => c.uploadEndpoint,
        dropTitle: 'Drag & drop media here',
        dropSub: 'or click to browse — PNG, JPG, GIF, WebP, MP4',
        noun: 'media',
        matches: (f) => f.type.startsWith('image/') || f.type === 'video/mp4' || /\.mp4$/i.test(f.name),
    },
    // Standalone CDN icons (admin Upload Icons page): no item_id / DB / gallery — files go straight
    // to the CDN and come back as plain {name,url}. Accepts SVG on top of the raster image types.
    icon: {
        accept: '.png,.svg,.gif,.jpg,.jpeg,.webp,image/png,image/svg+xml,image/gif,image/jpeg,image/webp',
        fieldName: 'icon_files[]',
        endpoint: (c) => c.uploadEndpoint,
        dropTitle: 'Drag & drop icons here',
        dropSub: 'or click to browse — PNG, SVG, GIF, JPG, WebP',
        noun: 'icon',
        matches: (f) => /\.(png|svg|gif|jpe?g|webp)$/i.test(f.name) || f.type.startsWith('image/'),
    },
};

// The target item id: the page item, else whatever the Tom Select picker (#item_id) currently holds.
function resolveItemId(preset: string): string {
    if (preset) return preset;
    const picker = document.getElementById('item_id') as HTMLInputElement | HTMLSelectElement | null;
    return (picker?.value || '').trim();
}

// Surface an upload problem everywhere it can be seen: the inline status line (always present in
// the Upload pane) plus, when the page provides them, the shared side-log warning bar + panel
// (combat/admin chrome). displayWarning() / openSideLog() only exist where template.js's side
// panels are loaded, so both are feature-detected — on the Media Manager they're simply skipped.
function surfaceUploadError(inline: HTMLElement, message: string): void {
    inline.className = 'small text-danger';
    inline.textContent = message;
    if (typeof window.displayWarning === 'function') {
        window.displayWarning(message);
        if (typeof window.openSideLog === 'function') window.openSideLog();
    }
}

interface UploadResult {
    status: number;
    text: string;
}

// POST a FormData with real upload progress. fetch() can't report request-body progress, so the
// uploader uses XMLHttpRequest: upload.onprogress drives the bar while bytes go up, then the load
// event hands back the raw status + body text (parsed defensively by the caller). Resolves on any
// HTTP response (including 4xx/5xx); rejects only on a transport-level failure.
function postFormWithProgress(
    url: string,
    body: FormData,
    onProgress: (pct: number | null) => void,
    onOpen?: (abort: () => void) => void,
): Promise<UploadResult> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        // Hand the caller an abort handle so an in-flight upload can be canceled (Cancel button).
        if (onOpen) onOpen(() => xhr.abort());
        xhr.upload.addEventListener('progress', (e) => {
            onProgress(e.lengthComputable ? Math.round((e.loaded / e.total) * 100) : null);
        });
        // Bytes are all up; the server is now analyzing + pushing to the CDN. Hold at 100 (the bar
        // stays striped/animated) until the response lands.
        xhr.upload.addEventListener('load', () => onProgress(100));
        xhr.addEventListener('load', () => resolve({ status: xhr.status, text: xhr.responseText }));
        xhr.addEventListener('error', () => reject(new Error('network error')));
        xhr.addEventListener('abort', () => reject(new Error('upload aborted')));
        xhr.addEventListener('timeout', () => reject(new Error('upload timed out')));
        xhr.send(body);
    });
}

export function mountUploader(host: HTMLElement, hooks: UploaderHooks): void {
    const { config, kind } = hooks;
    const spec = SPECS[kind];
    host.innerHTML = '';

    const dropzone = document.createElement('div');
    dropzone.className = 'ft-media-dropzone';
    dropzone.setAttribute('role', 'button');
    dropzone.tabIndex = 0;
    dropzone.innerHTML =
        '<div class="ft-media-dropzone-inner">' +
        '<div class="ft-media-dropzone-title"></div>' +
        '<div class="ft-media-dropzone-sub"></div>' +
        '</div>';
    (dropzone.querySelector('.ft-media-dropzone-title') as HTMLElement).textContent = spec.dropTitle;
    (dropzone.querySelector('.ft-media-dropzone-sub') as HTMLElement).textContent = spec.dropSub;

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = spec.accept;
    input.className = 'd-none';

    const previews = document.createElement('div');
    previews.className = 'row text-left mt-3';

    // Upload progress bar (Bootstrap). Hidden until an upload is in flight.
    const progressWrap = document.createElement('div');
    progressWrap.className = 'progress mt-3';
    progressWrap.style.display = 'none';
    const progressBar = document.createElement('div');
    progressBar.className = 'progress-bar progress-bar-striped progress-bar-animated bg-primary';
    progressBar.setAttribute('role', 'progressbar');
    progressBar.style.width = '0%';
    progressWrap.appendChild(progressBar);

    const setProgress = (pct: number | null): void => {
        if (pct === null) {
            progressWrap.style.display = 'none';
            progressBar.style.width = '0%';
            progressBar.textContent = '';
            return;
        }
        progressWrap.style.display = '';
        progressBar.style.width = pct + '%';
        // At 100% the bytes are up but the server is still working — keep it animated, no number.
        progressBar.textContent = pct < 100 ? pct + '%' : 'Processing…';
        progressBar.setAttribute('aria-valuenow', String(pct));
    };

    const actions = document.createElement('div');
    actions.className = 'mt-3 d-flex align-items-center gap-3';
    const uploadBtn = document.createElement('button');
    uploadBtn.type = 'button';
    uploadBtn.className = 'btn btn-primary';
    uploadBtn.textContent = 'Upload';
    uploadBtn.disabled = true;
    // Dual-purpose button: "Clear all" (drops every staged file) when idle, "Cancel" (aborts the
    // in-flight request) while an upload is running. Hidden until something is loaded.
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'btn btn-outline-secondary border';
    clearBtn.textContent = 'Clear all';
    clearBtn.style.display = 'none';
    const resultMsg = document.createElement('div');
    resultMsg.className = 'small text-muted';
    actions.appendChild(uploadBtn);
    actions.appendChild(clearBtn);
    actions.appendChild(resultMsg);

    host.appendChild(dropzone);
    host.appendChild(input);
    host.appendChild(previews);
    host.appendChild(progressWrap);
    host.appendChild(actions);

    let chosen: File[] = [];

    // Blob URLs minted for the current previews, so they can be revoked when a file is removed,
    // cleared, or the previews are rebuilt (video previews otherwise leak — they never auto-revoke).
    let previewUrls: string[] = [];
    const revokePreviewUrls = (): void => {
        previewUrls.forEach((u) => URL.revokeObjectURL(u));
        previewUrls = [];
    };

    // Drop one staged file (before upload) and re-render. Revoking happens in renderPreviews.
    const removeAt = (idx: number): void => {
        chosen.splice(idx, 1);
        resultMsg.textContent = '';
        renderPreviews();
    };

    const renderPreviews = (): void => {
        revokePreviewUrls();
        previews.innerHTML = '';
        chosen.forEach((file, idx) => {
            const col = document.createElement('div');
            col.className = 'col-md-3 p-2';

            // Thumbnail + an overlaid remove (×) button to clear this one file pre-upload.
            const frame = document.createElement('div');
            frame.className = 'position-relative';
            const url = URL.createObjectURL(file);
            previewUrls.push(url);
            if (kind === 'video' || (kind === 'media' && batchKind(file) === 'video')) {
                const vid = document.createElement('video');
                vid.className = 'img-thumbnail';
                vid.muted = true;
                vid.controls = true;
                vid.setAttribute('playsinline', '');
                vid.setAttribute('preload', 'metadata');
                vid.src = url;
                frame.appendChild(vid);
            } else {
                const img = document.createElement('img');
                img.className = 'img-thumbnail';
                img.src = url;
                img.alt = file.name;
                frame.appendChild(img);
            }
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'btn btn-sm btn-danger position-absolute top-0 end-0 m-1 py-0 px-2';
            remove.setAttribute('aria-label', `Remove ${file.name}`);
            remove.title = 'Remove';
            remove.textContent = '×';
            remove.addEventListener('click', () => removeAt(idx));
            frame.appendChild(remove);
            col.appendChild(frame);

            const cap = document.createElement('div');
            cap.className = 'small text-muted text-truncate';
            cap.textContent = file.name;
            col.appendChild(cap);
            previews.appendChild(col);
        });
        uploadBtn.disabled = chosen.length === 0;
        clearBtn.style.display = chosen.length === 0 ? 'none' : '';
    };

    const addFiles = (files: FileList | null): void => {
        if (!files) return;
        const accepted = Array.from(files).filter(spec.matches);
        chosen = chosen.concat(accepted);
        resultMsg.textContent = '';
        // Reset the native input so re-picking the same file (e.g. after removing it) re-fires change.
        input.value = '';
        renderPreviews();
    };

    dropzone.addEventListener('click', () => input.click());
    dropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
    });
    input.addEventListener('change', () => addFiles(input.files));

    ['dragenter', 'dragover'].forEach((evt) =>
        dropzone.addEventListener(evt, (e) => {
            e.preventDefault();
            dropzone.classList.add('ft-media-dropzone--over');
        }),
    );
    ['dragleave', 'drop'].forEach((evt) =>
        dropzone.addEventListener(evt, (e) => {
            e.preventDefault();
            dropzone.classList.remove('ft-media-dropzone--over');
        }),
    );
    dropzone.addEventListener('drop', (e) => addFiles((e as DragEvent).dataTransfer?.files ?? null));

    // Abort handle for the current upload (null when idle). Set via postFormWithProgress's onOpen.
    let aborter: (() => void) | null = null;

    // Flip the controls between idle and in-flight. While uploading, clearBtn becomes a red "Cancel"
    // that aborts the request; idle, it's the "Clear all" that drops staged files.
    const setUploadingUI = (uploading: boolean): void => {
        if (uploading) {
            clearBtn.style.display = '';
            clearBtn.textContent = 'Cancel';
            clearBtn.classList.remove('btn-outline-secondary');
            clearBtn.classList.add('btn-outline-danger');
        } else {
            aborter = null;
            clearBtn.textContent = 'Clear all';
            clearBtn.classList.remove('btn-outline-danger');
            clearBtn.classList.add('btn-outline-secondary');
            clearBtn.style.display = chosen.length === 0 ? 'none' : '';
            uploadBtn.disabled = chosen.length === 0;
        }
    };

    clearBtn.addEventListener('click', () => {
        // In-flight → cancel the upload (the XHR abort flows through the click handler's catch).
        if (aborter) { aborter(); return; }
        chosen = [];
        resultMsg.textContent = '';
        // Reset the native input so re-picking the same file(s) still fires a change event.
        input.value = '';
        renderPreviews();
    });

    const batchKind = (file: File): 'image' | 'video' => (
        file.type === 'video/mp4' || /\.mp4$/i.test(file.name) ? 'video' : 'image'
    );

    const parseUploadResponse = (
        uploadKind: 'image' | 'video' | 'icon',
        res: UploadResult,
    ): { count: number; errors: string[]; failedMessage: string | null } => {
        let parsed: unknown = null;
        try {
            parsed = JSON.parse(res.text);
        } catch {
            parsed = null;
        }
        if (!parsed || typeof parsed !== 'object') {
            const detail = res.text.trim().slice(0, 600) || `HTTP ${res.status}`;
            return {
                count: 0,
                errors: [],
                failedMessage: `Server returned a non-JSON response (HTTP ${res.status}): ${detail}`,
            };
        }

        const errs = [...((parsed as { errors?: string[] }).errors || [])];
        const debug = (parsed as { debug?: string | null }).debug;
        if (debug) errs.push(`Server notice: ${debug}`);

        if (uploadKind === 'video') {
            const data = parsed as VideoUploadResponse;
            const vids: ItemVideo[] = (data.uploaded || []).map((u) => ({
                video_id: u.video_id,
                url: u.url,
                mime: u.mime || 'video/mp4',
                name: u.name || '',
                description: u.description || '',
                primary: !!u.primary,
            }));
            if (vids.length) hooks.onUploadedVideos?.(vids);
            return { count: vids.length, errors: errs, failedMessage: null };
        }

        if (uploadKind === 'icon') {
            const data = parsed as IconUploadResponse;
            const icons: UploadedFile[] = (data.uploaded || []).map((u) => ({
                name: u.name || '',
                url: u.url || null,
            }));
            if (icons.length) hooks.onUploadedIcons?.(icons);
            return { count: icons.length, errors: errs, failedMessage: null };
        }

        const data = parsed as PictureUploadResponse;
        const pics: ItemPicture[] = (data.uploaded || []).map((u) => ({
            pic_id: u.pic_id,
            url: u.url,
            name: u.name || '',
            description: '',
            primary: !!u.primary,
        }));
        if (pics.length) hooks.onUploadedPictures?.(pics);
        return { count: pics.length, errors: errs, failedMessage: null };
    };

    const uploadBatch = async (
        uploadKind: 'image' | 'video' | 'icon',
        files: File[],
        itemId: string,
        onProgress: (pct: number | null) => void,
    ): Promise<{ count: number; errors: string[]; failedMessage: string | null }> => {
        const batchSpec = SPECS[uploadKind];
        const fd = new FormData();
        if (uploadKind !== 'icon') {
            fd.append('item_id', itemId);
            // The registry key the generic media endpoints (options/tools/media/) look up via
            // ft_media_registry() to pick the config (S3 prefix, attached_item_type, processing).
            // e.g. 'campaign' — NOT the picture attached type 'campaign_header'. 'item' for items.
            fd.append('type', config.mediaType);
        }
        fd.append('ajax', '1');
        files.forEach((file) => fd.append(batchSpec.fieldName, file, file.name));

        const res = await postFormWithProgress(
            batchSpec.endpoint(config),
            fd,
            onProgress,
            (abort) => { aborter = abort; },
        );
        return parseUploadResponse(uploadKind, res);
    };

    uploadBtn.addEventListener('click', async () => {
        // Icons are item-less (straight to the CDN); image/video uploads need a target item.
        const needsItem = kind !== 'icon';
        const itemId = needsItem ? resolveItemId(hooks.itemId) : '';
        if (needsItem && !itemId) {
            surfaceUploadError(resultMsg, 'Choose an item first.');
            return;
        }
        if (chosen.length === 0) return;

        uploadBtn.disabled = true;
        resultMsg.className = 'small text-muted';
        resultMsg.textContent = 'Uploading…';
        setProgress(0);
        setUploadingUI(true);

        try {
            const batches: Array<{ uploadKind: 'image' | 'video' | 'icon'; files: File[] }> = [];
            if (kind === 'media') {
                const images = chosen.filter((file) => batchKind(file) === 'image');
                const videos = chosen.filter((file) => batchKind(file) === 'video');
                if (images.length) batches.push({ uploadKind: 'image', files: images });
                if (videos.length) batches.push({ uploadKind: 'video', files: videos });
            } else {
                batches.push({ uploadKind: kind, files: chosen });
            }

            let count = 0;
            const errs: string[] = [];
            let failedMessage: string | null = null;
            for (let i = 0; i < batches.length; i += 1) {
                const batch = batches[i]!;
                resultMsg.textContent = batches.length > 1
                    ? `Uploading ${batch.uploadKind}s (${i + 1}/${batches.length})…`
                    : 'Uploading…';
                const result = await uploadBatch(batch.uploadKind, batch.files, itemId, setProgress);
                count += result.count;
                errs.push(...result.errors);
                if (result.failedMessage) {
                    failedMessage = result.failedMessage;
                    errs.push(result.failedMessage);
                }
            }
            setProgress(null);

            // Nothing landed AND we have a reason → that's a real failure: surface the reason.
            if (count === 0) {
                surfaceUploadError(
                    resultMsg,
                    failedMessage || (errs.length ? `Upload failed — ${errs.join(' · ')}` : 'Upload failed — no files were saved.'),
                );
                uploadBtn.disabled = chosen.length === 0;
                return;
            }

            // Some landed. Report success; if anything warned, flag it (and push to the side-log).
            resultMsg.className = errs.length ? 'small text-warning' : 'small text-success';
            resultMsg.textContent =
                `Uploaded ${count} ${spec.noun}${count === 1 ? '' : 's'}` +
                (errs.length ? ` — ${errs.length} issue${errs.length === 1 ? '' : 's'}.` : '.');
            if (errs.length && typeof window.displayWarning === 'function') {
                window.displayWarning(errs.join('<br>'));
            }

            chosen = [];
            renderPreviews();
        } catch (err) {
            setProgress(null);
            const message = (err as Error)?.message || 'network error';
            if (message === 'upload aborted') {
                // User hit Cancel — keep the staged files so they can retry, and don't alarm them.
                resultMsg.className = 'small text-muted';
                resultMsg.textContent = 'Upload canceled.';
            } else {
                console.error('[item-media] upload failed', err);
                surfaceUploadError(resultMsg, `Upload failed (${message}). Please try again.`);
            }
            uploadBtn.disabled = chosen.length === 0;
        } finally {
            // Restore the idle controls (Cancel → Clear all, re-enable Upload) on every outcome.
            setUploadingUI(false);
        }
    });
}
